import React, { useState } from 'react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import ContactInfo from './ContactInfo';
import ContactForm from './ContactForm';
import '../styles/Contact.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    challenge: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error'

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Lenient URL validation & normalization. Accepts: example.com, www.example.com,
  // http(s)://example.com. Empty is valid (field is optional). Rejects strings with
  // no dot / no domain structure like "asdf1234".
  const normalizeWebsite = (raw) => {
    const value = (raw || '').trim();
    if (!value) return { ok: true, normalized: '' };
    // Must contain at least one dot with letters on both sides (domain-like)
    const domainLike = /^(https?:\/\/)?(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+([\/?#].*)?$/i;
    if (!domainLike.test(value)) {
      return { ok: false, normalized: value };
    }
    const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    return { ok: true, normalized: withScheme };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const websiteCheck = normalizeWebsite(formData.website);
    if (!websiteCheck.ok) {
      toast({
        title: "Please check the Website URL",
        description: "Enter a domain like example.com or leave the field blank.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const payload = { ...formData, website: websiteCheck.normalized };

    try {
      const response = await axios.post(`${API}/contact`, payload);

      if (response.data.success) {
        toast({
          title: "Message Received!",
          description: response.data.message,
        });
        setSubmitStatus('success');

        // GA4 conversion tracking: push generate_lead event for GTM/GA4 to pick up
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'generate_lead',
          form_name: 'contact_form',
        });

        setFormData({
          name: '',
          email: '',
          phone: '',
          website: '',
          challenge: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Contact form error:', error);
      }
      setSubmitStatus('error');
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="contact-container">
        <div className="contact-content">
          <ContactInfo />
          <div className="contact-form-wrapper reveal">
            <ContactForm 
              formData={formData}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onChange={handleChange}
            />
            {submitStatus === 'success' && (
              <div className="contact-status-message contact-status-success" role="status" data-testid="contact-success-message">
                Thanks — your message is in. I'll reply within 24 hours.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="contact-status-message contact-status-error" role="alert" data-testid="contact-error-message">
                Something went wrong sending your message. Please try again, or email me directly at{' '}
                <a href="mailto:roy.san007@gmail.com">roy.san007@gmail.com</a>.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
